import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Typography, Button, Chip, Dialog, DialogTitle, DialogContent,
    DialogActions, IconButton,
} from '@mui/material';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import SlipPdfFrame from './slipPdfFrame';
import PdfBody from './slipPdfBody';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../../context/userContext';
import { getAllSlips } from '../../api/slipApi';
import {
    voidSlip as voidCommissionSlip,
    getSlipById as getCommissionSlipById,
    approveSlip as approveCommissionSlip,
    rejectSlip as rejectCommissionSlip,
    bulkGenerateSlips,
    exportMakerCsv,
} from '../../api/commissionSlipApi';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {
    voidIncentiveSlip,
    getAllIncentiveSlips,
    approveIncentiveSlip,
} from '../../api/incentiveSlipApi';
import {
    voidExtraSlip,
    getAllExtraSlips,
    approveExtraSlip,
    rejectExtraSlip,
} from '../../api/extraSlipApi';
import CreateExtraSlipDialog from './createExtraSlipDialog';
import IncentiveClaimSlipsList from './incentiveClaimSlipsList';

const TYPE_FILTERS = ['All', 'Commission', 'Incentive', 'Other'];
const STATUS_FILTERS = ['All', 'Pending', 'Approved', 'Voided'];

const formatRM = (amount) => `RM ${new Intl.NumberFormat('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0)}`;
const formatDate = (dateStr) => dateStr
    ? new Date(dateStr).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' })
    : '-';

const statusConfig = {
    Approved: { bg: '#D1FAE5', color: '#065F46' },
    Pending: { bg: '#FEF3C7', color: '#92400E' },
    Voided: { bg: '#FEE2E2', color: '#991B1B' },
    Rejected: { bg: '#F1F5F9', color: '#64748B' },
};

const typeColors = {
    Commission: { bg: '#DBEAFE', color: '#1E3A8A' },
    Incentive: { bg: '#FCE7F3', color: '#9D174D' },
    Other: { bg: '#F3E8FF', color: '#6B21A8' },
};

export default function AllSlipsList() {
    const { user } = useUser();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const initialType = searchParams.get('type');
    const [activeType, setActiveType] = useState(
        TYPE_FILTERS.includes(initialType) ? initialType : 'All'
    );
    const [statusFilter, setStatusFilter] = useState('All');
    // Top-level mode toggle: existing commission/incentive/other slip pipeline
    // vs the new Incentive Claim slip pipeline (cash payout + item fulfilment).
    const [slipMode, setSlipMode] = useState('Commission');

    const [slips, setSlips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [viewSlip, setViewSlip] = useState(null);
    const [renderSlip, setRenderSlip] = useState(null); // currently being rendered for PDF
    const slipRenderRef = useRef(null);
    const [generatePeriod, setGeneratePeriod] = useState(dayjs());
    const [generating, setGenerating] = useState(false);

    const canCreateExtra = !!user?.roleRights?.includes('CreateExtraSlip');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getAllSlips(activeType, 'all');
            setSlips(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Failed to load slips:', e);
            setSlips([]);
        } finally {
            setLoading(false);
        }
    }, [activeType]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleTypeChange = (next) => {
        setActiveType(next);
        const params = new URLSearchParams(searchParams);
        if (next === 'All') params.delete('type'); else params.set('type', next);
        setSearchParams(params, { replace: true });
    };

    // Status filter applied client-side after type filter
    const filteredSlips = useMemo(
        () => statusFilter === 'All' ? slips : slips.filter(s => s.status === statusFilter),
        [slips, statusFilter]
    );

    // Counts per status for the pills (based on the type-filtered set)
    const statusCounts = useMemo(() => {
        const c = { All: slips.length, Pending: 0, Approved: 0, Voided: 0 };
        slips.forEach(s => {
            if (c[s.status] !== undefined) c[s.status]++;
        });
        return c;
    }, [slips]);

    // Hydrates the lightweight aggregate row into the full type-specific
    // slip payload. Used for both View and PDF actions.
    const hydrate = async (row) => {
        if (row.type === 'Commission') {
            const res = await getCommissionSlipById(row.slipId);
            return { ...row, ...res.data, type: 'Commission' };
        }
        if (row.type === 'Incentive') {
            const res = await getAllIncentiveSlips();
            const match = (res.data || []).find(s => s.incentiveSlipId === row.slipId);
            return match ? { ...row, ...match, type: 'Incentive' } : row;
        }
        // Other = ExtraSlip
        const list = await getAllExtraSlips();
        const match = (list || []).find(s => s.extraSlipId === row.slipId);
        return match ? { ...row, ...match, type: 'Other' } : row;
    };

    const handleView = async (row) => {
        try {
            const full = await hydrate(row);
            setViewSlip(full);
        } catch (e) {
            console.error('Failed to load slip details:', e);
            alert('Failed to load slip details');
        }
    };

    const handleGenerate = async () => {
        const month = generatePeriod.month() + 1;
        const year = generatePeriod.year();
        const label = generatePeriod.format('MMMM YYYY');
        if (!window.confirm(`Generate commission slips for ${label}?\n\nThis creates slips for all agents with payouts in this period who don't already have a Pending or Approved slip.`)) return;
        setGenerating(true);
        try {
            const res = await bulkGenerateSlips(month, year);
            const d = res.data || {};
            const created = d.created ?? d.Created ?? 0;
            const skipped = d.skipped ?? d.Skipped ?? 0;
            alert(`Generated ${created} new slip(s)${skipped ? `. Skipped ${skipped} (already had Pending/Approved).` : '.'}`);
            fetchData();
        } catch (e) {
            alert('Failed to generate: ' + (e.response?.data || e.message));
        } finally {
            setGenerating(false);
        }
    };

    const handleApprove = async (slip) => {
        if (!window.confirm(`Approve this ${slip.type} slip?`)) return;
        try {
            if (slip.type === 'Commission') {
                await approveCommissionSlip(slip.slipId);
            } else if (slip.type === 'Incentive') {
                await approveIncentiveSlip(slip.slipId ?? slip.incentiveSlipId);
            } else {
                await approveExtraSlip(slip.slipId ?? slip.extraSlipId, '');
            }
            setViewSlip(null);
            fetchData();
        } catch (e) {
            alert('Failed to approve: ' + (e.response?.data || e.message));
        }
    };

    const handleReject = async (slip) => {
        const remarks = window.prompt(`Reason for rejecting this ${slip.type} slip?`);
        if (remarks === null) return;
        if (!remarks.trim()) { alert('A reason is required to reject.'); return; }
        try {
            if (slip.type === 'Commission') {
                await rejectCommissionSlip(slip.slipId, remarks);
            } else if (slip.type === 'Other') {
                await rejectExtraSlip(slip.slipId ?? slip.extraSlipId, remarks);
            } else {
                // Incentive: no dedicated reject API - fall back to void with the remarks.
                await voidIncentiveSlip(slip.slipId ?? slip.incentiveSlipId, remarks);
            }
            setViewSlip(null);
            fetchData();
        } catch (e) {
            alert('Failed to reject: ' + (e.response?.data || e.message));
        }
    };

    // Bulk: download PDF for each selected row sequentially (use the same
    // hydrate + render pipeline as single-row PDF).
    const handleBulkDownload = async (rows) => {
        for (const r of rows) {
            // eslint-disable-next-line no-await-in-loop
            await handlePdf(r.original);
        }
    };

    // Bulk approve: filter to actionable Pending rows so a mixed selection
    // (some already-Approved or Voided) still works on the Pending subset.
    const handleBulkApprove = async (rows) => {
        const pending = rows.filter(r => (r.original.status || 'Pending') === 'Pending');
        const skipped = rows.length - pending.length;
        if (!pending.length) {
            alert('No Pending slips selected.');
            return;
        }
        const skipMsg = skipped ? `\n(${skipped} non-pending row(s) will be skipped.)` : '';
        if (!window.confirm(`Approve ${pending.length} slip(s)?${skipMsg}`)) return;
        const errors = [];
        for (const r of pending) {
            const s = r.original;
            try {
                // eslint-disable-next-line no-await-in-loop
                if (s.type === 'Commission') await approveCommissionSlip(s.slipId);
                else if (s.type === 'Incentive') await approveIncentiveSlip(s.slipId);
                else await approveExtraSlip(s.slipId, '');
            } catch (e) {
                errors.push(`${s.agentName ?? s.slipId}: ${e.response?.data || e.message}`);
            }
        }
        if (errors.length) alert(`Approved with ${errors.length} error(s):\n${errors.join('\n')}`);
        else alert(`${pending.length} slip(s) approved.`);
        fetchData();
    };

    // Bulk Maker CSV: only meaningful for Approved Commission slips.
    const handleBulkMakerCsv = async (rows) => {
        const eligible = rows
            .filter(r => r.original.type === 'Commission' && r.original.status === 'Approved')
            .map(r => r.original.slipId);
        if (!eligible.length) {
            alert('Maker CSV is only generated for Approved Commission slips. None of the selected rows match.');
            return;
        }
        try {
            const res = await exportMakerCsv(eligible);
            const data = res.data || [];
            if (!data.length) { alert('No data to export.'); return; }
            const headers = ['No', 'Billing Name', 'Bank Name', 'Bank Account No', 'Amount', 'Reference', 'Period', 'Remarks'];
            // Bank Account No must stay as text in Excel - Excel auto-coerces
            // anything that looks like a long number into scientific notation
            // (1.57019E+11 etc.). Wrapping the cell as `="..."` is the
            // standard CSV-to-Excel hint to preserve it verbatim.
            const escape = (v) => {
                const s = (v ?? '').toString().replace(/"/g, '""');
                return `"${s}"`;
            };
            const escapeAsText = (v) => {
                const s = (v ?? '').toString().replace(/"/g, '""');
                return `="${s}"`;
            };
            const csvRows = data.map(d => [
                escape(d.no),
                escape(d.billingName),
                escape(d.bankName),
                escapeAsText(d.bankAccountNo),
                escape(d.amount),
                escape(d.reference),
                escape(d.period),
                escape(d.remarks),
            ]);
            // Prepend BOM so Excel opens UTF-8 correctly (preserves accented names).
            const csv = 'ï»¿' + [headers.map(escape).join(','), ...csvRows.map(r => r.join(','))].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Maker_List_${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            alert('Export failed: ' + (e.response?.data || e.message));
        }
    };

    // Generate PDF in-place using the shared SlipPdfFrame.
    const handlePdf = async (row) => {
        try {
            const full = await hydrate(row);
            setRenderSlip(full);
            // Wait one frame for off-screen DOM to mount
            await new Promise(r => setTimeout(r, 100));
            const element = slipRenderRef.current;
            if (!element) throw new Error('Render target missing');
            const canvas = await html2canvas(element, { scale: 2.5, backgroundColor: '#ffffff', logging: false, useCORS: true });
            const imgData = canvas.toDataURL('image/png');
            const pdfWidth = (canvas.width * 25.4) / (96 * 2.5);
            const pdfHeight = (canvas.height * 25.4) / (96 * 2.5);
            const pdf = new jsPDF({ orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait', unit: 'mm', format: [pdfWidth, pdfHeight] });
            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
            const safeAgent = (full.agentName || 'agent').replace(/\s+/g, '_');
            const safePeriod = (full.period || 'period').replace(/\s+/g, '_');
            pdf.save(`${full.type}_Slip_${safeAgent}_${safePeriod}.pdf`);
        } catch (e) {
            console.error('PDF generation failed:', e);
            alert('Failed to generate PDF: ' + e.message);
        } finally {
            setRenderSlip(null);
        }
    };

    const handleRemove = async (slip) => {
        const reason = window.prompt(`Reason for voiding this ${slip.type} slip?`);
        if (reason === null) return; // cancelled
        if (!reason.trim()) { alert('A reason is required to void.'); return; }
        try {
            if (slip.type === 'Commission') {
                await voidCommissionSlip(slip.slipId, reason);
            } else if (slip.type === 'Incentive') {
                await voidIncentiveSlip(slip.slipId, reason);
            } else {
                await voidExtraSlip(slip.slipId, reason);
            }
            fetchData();
        } catch (e) {
            alert('Failed to void: ' + (e.response?.data || e.message));
        }
    };

    const isSuperAdmin = user?.roles?.includes('Super Admin');

    const columns = useMemo(() => [
        {
            id: 'no', header: 'No', size: 60, enableSorting: false, enableColumnFilter: false,
            Cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.index + 1}</span>,
        },
        {
            id: 'status', header: 'Status', accessorKey: 'status', size: 110,
            Cell: ({ cell }) => {
                const val = cell.getValue() || 'Pending';
                const cfg = statusConfig[val] || statusConfig.Pending;
                return <Chip label={val} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", backgroundColor: cfg.bg, color: cfg.color }} />;
            },
            filterVariant: 'select',
            filterSelectOptions: ['Approved', 'Pending', 'Voided', 'Rejected'],
        },
        {
            id: 'type', header: 'Type', accessorKey: 'type', size: 110,
            Cell: ({ cell }) => {
                const v = cell.getValue() || '';
                const cfg = typeColors[v] || { bg: '#F1F5F9', color: '#64748B' };
                return <Chip label={v} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", backgroundColor: cfg.bg, color: cfg.color }} />;
            },
            filterVariant: 'select',
            filterSelectOptions: ['Commission', 'Incentive', 'Other'],
        },
        {
            id: 'name', header: 'Name', accessorKey: 'displayName', size: 260,
            Cell: ({ row }) => (
                <span style={{ fontWeight: 600, color: '#0F172A' }}>{row.original.displayName}</span>
            ),
        },
        {
            id: 'totalPayout', header: 'Total Payout', accessorKey: 'totalPayout', size: 130,
            Cell: ({ row }) => <span style={{ fontWeight: 600 }}>{formatRM(row.original.totalPayout)}</span>,
        },
        { id: 'agentName', header: 'Agent Name', accessorKey: 'agentName', size: 160 },
        { id: 'period', header: 'Period', accessorKey: 'period', size: 180 },
        {
            id: 'dateIssued', header: 'Date Issued', accessorKey: 'dateIssued', size: 120,
            Cell: ({ row }) => formatDate(row.original.dateIssued),
        },
        {
            id: 'actions', header: '', size: 220, enableSorting: false, enableColumnFilter: false,
            Cell: ({ row }) => {
                const s = row.original;
                const canApprove = s.status === 'Pending';
                const canVoid = isSuperAdmin && s.status === 'Approved';
                return (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon sx={{ fontSize: '0.85rem !important' }} />}
                            onClick={() => handleView(s)}
                            sx={{ textTransform: 'none', fontSize: '0.72rem', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', minWidth: 0, px: 1.2 }}
                        >
                            View
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon sx={{ fontSize: '0.85rem !important' }} />}
                            onClick={() => handlePdf(s)}
                            sx={{ textTransform: 'none', fontSize: '0.72rem', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', minWidth: 0, px: 1.2, color: '#64748B', borderColor: '#CBD5E1' }}
                        >
                            PDF
                        </Button>
                        {canApprove && (
                            <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => handleApprove(s)}
                                sx={{ textTransform: 'none', fontSize: '0.72rem', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', minWidth: 0, px: 1.2, boxShadow: 'none' }}
                            >
                                Approve
                            </Button>
                        )}
                        {canVoid && (
                            <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => handleRemove(s)}
                                sx={{ textTransform: 'none', fontSize: '0.72rem', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', minWidth: 0, px: 1.2 }}
                            >
                                Void
                            </Button>
                        )}
                    </Box>
                );
            },
        },
    ], [navigate, isSuperAdmin]);

    const table = useMaterialReactTable({
        columns,
        data: filteredSlips,
        enableRowSelection: true,
        getRowId: (row) => `${row.type}-${row.slipId}`,
        enableColumnActions: false,
        enableColumnDragging: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
        columnFilterDisplayMode: 'popover',
        initialState: { density: 'compact', showGlobalFilter: true },
        state: { isLoading: loading },
        muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
        muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
        muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
        muiTableBodyRowProps: { sx: { '&:hover': { backgroundColor: '#F8FAFC' } } },
        renderTopToolbarCustomActions: ({ table }) => {
            const selected = table.getSelectedRowModel().rows;
            if (!selected.length) return null;
            const anyPending = selected.some(r => (r.original.status || 'Pending') === 'Pending');
            const anyApprovedCommission = selected.some(r => r.original.type === 'Commission' && r.original.status === 'Approved');
            return (
                <Box sx={{ display: 'flex', gap: 1, p: 1, flexWrap: 'wrap' }}>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleBulkDownload(selected)}
                        sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.75rem', fontWeight: 600, borderRadius: '8px', background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', boxShadow: 'none' }}
                    >
                        Download Selected ({selected.length})
                    </Button>
                    {anyPending && (
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleBulkApprove(selected)}
                            sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.75rem', fontWeight: 600, borderRadius: '8px', background: 'linear-gradient(135deg, #10B981, #059669)', boxShadow: 'none' }}
                        >
                            Approve Selected ({selected.filter(r => (r.original.status || 'Pending') === 'Pending').length})
                        </Button>
                    )}
                    {anyApprovedCommission && (
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleBulkMakerCsv(selected)}
                            sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.75rem', fontWeight: 600, borderRadius: '8px', background: 'linear-gradient(135deg, #6366F1, #4F46E5)', boxShadow: 'none' }}
                        >
                            Export Maker CSV ({selected.filter(r => r.original.type === 'Commission' && r.original.status === 'Approved').length})
                        </Button>
                    )}
                </Box>
            );
        },
    });

    const pillStyle = (active) => ({
        border: active ? '1px solid #1A8CFF' : '1px solid #E2E8F0',
        background: active ? 'linear-gradient(135deg, #1A8CFF, #0F62CC)' : 'white',
        color: active ? 'white' : '#475569',
        borderRadius: '8px',
        padding: '6px 16px',
        fontSize: '0.8rem',
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: "'Poppins', sans-serif",
        transition: 'all 0.15s ease',
    });

    return (
        <div>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>Slips</h2>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>
                        Manage all slip payouts - commission, incentive and one-off "other"
                    </p>
                </div>
                {slipMode === 'Commission' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <DatePicker
                        views={['month', 'year']}
                        value={generatePeriod}
                        onChange={(val) => val && setGeneratePeriod(val)}
                        slotProps={{ textField: { size: 'small', sx: { width: 160, '& .MuiInputBase-root': { fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' } } } }}
                    />
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleGenerate}
                        disabled={generating}
                        sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', fontWeight: 600, borderRadius: '8px', boxShadow: 'none' }}
                    >
                        {generating ? 'Generating...' : 'Generate Commission Slips'}
                    </Button>
                    {canCreateExtra && (
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => setCreateOpen(true)}
                            sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', fontWeight: 600, borderRadius: '8px', background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', boxShadow: 'none' }}
                        >
                            + Create Extra Slip
                        </Button>
                    )}
                </Box>
                )}
            </Box>

            {/* Mode toggle: Commission pipeline vs Incentive Claim slips */}
            <Box sx={{ display: 'flex', mb: 2, borderRadius: '8px', overflow: 'hidden', border: '1px solid #E2E8F0', width: 'fit-content' }}>
                <button onClick={() => setSlipMode('Commission')} style={{ padding: '6px 18px', fontSize: '0.78rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", border: 'none', cursor: 'pointer', background: slipMode === 'Commission' ? '#1A8CFF' : '#fff', color: slipMode === 'Commission' ? '#fff' : '#64748B' }}>
                    Commission
                </button>
                <button onClick={() => setSlipMode('Incentive')} style={{ padding: '6px 18px', fontSize: '0.78rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", border: 'none', borderLeft: '1px solid #E2E8F0', cursor: 'pointer', background: slipMode === 'Incentive' ? '#1A8CFF' : '#fff', color: slipMode === 'Incentive' ? '#fff' : '#64748B' }}>
                    Incentive
                </button>
            </Box>

            {slipMode === 'Incentive' ? (
                <IncentiveClaimSlipsList />
            ) : (
            <>
            {/* Type pills */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', fontWeight: 600, mr: 0.5, fontFamily: "'Poppins', sans-serif", textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Type:
                </Typography>
                {TYPE_FILTERS.map(filter => (
                    <button key={filter} onClick={() => handleTypeChange(filter)} style={pillStyle(activeType === filter)}>
                        {filter}
                    </button>
                ))}
            </Box>

            {/* Status pills with counts */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', fontWeight: 600, mr: 0.5, fontFamily: "'Poppins', sans-serif", textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Status:
                </Typography>
                {STATUS_FILTERS.map(filter => (
                    <button key={filter} onClick={() => setStatusFilter(filter)} style={pillStyle(statusFilter === filter)}>
                        {filter} ({statusCounts[filter] ?? 0})
                    </button>
                ))}
            </Box>

            <MaterialReactTable table={table} />

            <CreateExtraSlipDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={fetchData}
            />

            {/* Inline detail dialog for "Other" slips */}
            {/* Unified rich-preview dialog - same SlipPdfFrame the PDF uses,
                rendered visibly so the user sees exactly what they'll get.
                Action buttons in the footer cover Approve / Reject / Void /
                Download PDF based on slip status + role. */}
            <Dialog open={!!viewSlip} onClose={() => setViewSlip(null)} fullWidth maxWidth="lg">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: "'Poppins', sans-serif" }}>
                    {viewSlip?.type ?? ''} Slip Preview
                    <IconButton size="small" onClick={() => setViewSlip(null)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ background: '#F8FAFC', p: 3 }}>
                    {viewSlip && (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{ boxShadow: '0 4px 24px rgba(15,23,42,0.08)' }}>
                                <SlipPdfFrame
                                    title={`${viewSlip.type} Slip`}
                                    agentName={viewSlip.agentName}
                                    period={viewSlip.period}
                                    dateIssued={viewSlip.dateIssued}
                                    totalPayout={viewSlip.totalPayout || viewSlip.amount}
                                    status={viewSlip.status}
                                    sectionTitle={
                                        viewSlip.type === 'Commission' ? 'Commission Breakdown' :
                                        viewSlip.type === 'Incentive' ? 'Incentive Achievement' :
                                        'Payment Details'
                                    }
                                >
                                    <PdfBody slip={viewSlip} />
                                </SlipPdfFrame>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
                    <Button onClick={() => setViewSlip(null)} sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif" }}>Close</Button>
                    {viewSlip && (
                        <Button
                            onClick={() => handlePdf(viewSlip)}
                            startIcon={<DownloadIcon />}
                            variant="outlined"
                            sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", borderRadius: '8px' }}
                        >
                            Download PDF
                        </Button>
                    )}
                    {viewSlip?.status === 'Pending' && (
                        <>
                            <Button
                                onClick={() => handleReject(viewSlip)}
                                variant="outlined"
                                color="error"
                                sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", borderRadius: '8px' }}
                            >
                                Reject
                            </Button>
                            <Button
                                onClick={() => handleApprove(viewSlip)}
                                variant="contained"
                                color="success"
                                sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', boxShadow: 'none' }}
                            >
                                Approve
                            </Button>
                        </>
                    )}
                    {viewSlip?.status === 'Approved' && isSuperAdmin && (
                        <Button
                            onClick={() => handleRemove(viewSlip)}
                            variant="outlined"
                            color="error"
                            sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", borderRadius: '8px' }}
                        >
                            Void
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Off-screen render target for PDF generation */}
            {renderSlip && (
                <div style={{ position: 'fixed', left: '-9999px', top: 0 }}>
                    <SlipPdfFrame
                        ref={slipRenderRef}
                        title={`${renderSlip.type} Slip`}
                        agentName={renderSlip.agentName}
                        period={renderSlip.period}
                        dateIssued={renderSlip.dateIssued}
                        totalPayout={renderSlip.totalPayout || renderSlip.amount}
                        status={renderSlip.status}
                        sectionTitle={
                            renderSlip.type === 'Commission' ? 'Commission Breakdown' :
                            renderSlip.type === 'Incentive' ? 'Incentive Achievement' :
                            'Payment Details'
                        }
                    >
                        <PdfBody slip={renderSlip} />
                    </SlipPdfFrame>
                </div>
            )}
            </>
            )}
        </div>
    );
}
